import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { sv } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAdminAttention, type AdminAttentionItem } from '@/hooks/useAdminAttention';
import { cn } from '@/lib/utils';

function AttentionItem({
  item,
  onNavigate,
}: {
  item: AdminAttentionItem;
  onNavigate: (href: string) => void;
}) {
  return (
    <button
      onClick={() => onNavigate(item.href)}
      className="w-full text-left px-3 py-2.5 hover:bg-muted/50 transition-colors rounded-md flex flex-col gap-0.5"
    >
      <span className="text-sm font-medium text-foreground">{item.title}</span>
      <span className="text-xs text-muted-foreground line-clamp-1">{item.subtitle}</span>
      <span className="text-[11px] text-muted-foreground/70">
        {formatDistanceToNow(new Date(item.created_at), { addSuffix: true, locale: sv })}
      </span>
    </button>
  );
}

/**
 * Admin attention bell icon with badge + popover panel.
 * Shows pending work items (applications, place suggestions).
 */
export function AdminAttentionBell() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { items, totalCount, isLoading } = useAdminAttention();

  const handleNavigate = (href: string) => {
    setOpen(false);
    navigate(href);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-9 w-9"
          data-testid="admin-attention-bell"
          aria-label="Kräver uppmärksamhet"
        >
          <Bell className="h-4.5 w-4.5" />
          {totalCount > 0 && (
            <Badge
              data-testid="admin-attention-badge"
              className={cn(
                'absolute -top-0.5 -right-0.5 h-4.5 min-w-[18px] px-1 text-[10px] leading-none',
                'flex items-center justify-center pointer-events-none',
                'bg-destructive text-destructive-foreground border-0'
              )}
            >
              {totalCount > 99 ? '99+' : totalCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 p-0"
        sideOffset={8}
      >
        <div className="px-4 py-3 border-b border-border">
          <h3 className="text-sm font-semibold text-foreground">Åtgärder</h3>
          {totalCount > 0 && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {totalCount} {totalCount === 1 ? 'ärende' : 'ärenden'}
            </p>
          )}
        </div>
        <ScrollArea className="max-h-[320px]">
          {isLoading ? (
            <div className="px-4 py-6 text-center text-sm text-muted-foreground">
              Laddar…
            </div>
          ) : items.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-muted-foreground">
              Inget kräver åtgärd just nu.
            </div>
          ) : (
            <div className="p-1.5 space-y-0.5">
              {items.map((item) => (
                <AttentionItem
                  key={item.item_id}
                  item={item}
                  onNavigate={handleNavigate}
                />
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
